// Utility selection (§10.1 Brain, §10.2): runs every TUNE.bot.thinkEveryTicks ticks per bot,
// staggered by slot, or early when a tactic flags `forceThink`. Every option is offered through
// `consider` (which applies the +15% hysteresis and the hard max-2-bots-per-target rule); the
// winner becomes the goal through `setGoal`, held for at least TUNE.bot.minGoalTicks unless it
// stops being possible. RECOVER is not an option here: it is checked every tick and always wins
// (manager.ts). Mode options (§10.4) come from modeTactics.ts.
import { NO_SLOT, TUNE } from '../constants';
import { BS, EK, ItemKind, MapId, ModeId, RoundPhase } from '../ids';
import { chance } from '../rng';
import { hasBody } from '../sim/bean';
import { BOMB_LIT } from '../sim/bomb';
import type { Entity, Vec2 } from '../types';
import { Goal, goalTargetsBean } from './brain';
import { gauss, pow07, type Ctx } from './context';
import { consider, createChoice, othersTargeting, setGoal, type Choice } from './goals';
import { crownOptions, lobbyOptions, podiumOptions, potatoOptions } from './modeTactics';
import { bestSafeSpot, isEnemy } from './tactics';
import { BOT_TUNE } from './tuning';

const A = TUNE.bot.attack;
const TH = BOT_TUNE.think;
const choice = createChoice();
const SPOT: Vec2 = { x: 0, y: 0 };

/** §10.2 exposure of target `slot` seen from the bot: 0.2 + (1 − voidRayDist/8)^0.7, weighted. */
export function exposure(ctx: Ctx, slot: number): number {
  const b = ctx.self;
  const o = ctx.seen[slot];
  let vr = ctx.field.voidRayDist(ctx.w, o.x, o.y, o.x - b.pos.x, o.y - b.pos.y, TUNE.bot.rayMax);
  // A bumper behind the target launches it like a ledge would (Pinball's real kill zones).
  const lr = ctx.field.launchRayDist(o.x, o.y, o.x - b.pos.x, o.y - b.pos.y, TUNE.bot.rayMax) + TH.launchRayBias;
  if (lr < vr) vr = lr;
  const raw = 1 - vr / TUNE.bot.rayMax;
  return A.exposureBase + pow07(raw < 0 ? 0 : raw > 1 ? 1 : raw) * ctx.brain.p.exposureWeight;
}

/** Mean match score of the beans with a body other than the bot (for "prefers the strongest"). */
function meanScore(ctx: Ctx): number {
  let n = 0;
  let sum = 0;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === ctx.self.slot || !ctx.seen[s].body) continue;
    sum += ctx.bc.matchScores[s] ?? 0;
    n++;
  }
  return n > 0 ? sum / n : 0;
}

/** ATTACK(T) utility (§10.2) with the personality specials of §10.5; ≤ 0 means "not a target". */
export function attackUtility(ctx: Ctx, slot: number, mean: number): number {
  const b = ctx.self;
  const br = ctx.brain;
  const p = br.p;
  const o = ctx.seen[slot];
  const t = ctx.w.beans[slot];
  if (!o.body || !hasBody(t) || t.invulnTicks > 0 || o.state === BS.SPUN || o.state === BS.RESPAWN) return 0;
  if (o.state === BS.FLOAT && Math.abs(o.z - b.z) > TUNE.balloon.hitDz + TUNE.jump.vz * 0.15) return 0;
  const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
  const prox = Math.max(A.proximityMin, Math.min(1, 1 - d / A.proximityRange));
  const exp = exposure(ctx, slot);
  // Opportunist: joins a fight only when the target is already exposed (KO stealer).
  if (p.minExposure > 0 && exp <= p.minExposure && o.state !== BS.LEDGE && o.state !== BS.TEETER) return 0;
  let u = p.aggression * prox * exp * (1 + t.dizzy / 100);
  if (slot === ctx.bc.crowned) u *= A.bountyMult;
  if (slot === br.grudge && ctx.w.tick < br.grudgeUntil) u *= A.grudgeMult;
  if (slot === ctx.leader) u *= p.leaderFocus;
  if (p.prefersStrongest) u *= 1 + Math.min(TH.strongestMax, Math.max(0, ((ctx.bc.matchScores[slot] ?? 0) - mean) * TH.strongestPerPoint));
  if (slot === br.lastHitBy && ctx.w.tick - br.lastHitByTick < TH.retaliateTicks) u *= TH.retaliateMult;
  const others = othersTargeting(ctx, slot, br);
  if (others >= TUNE.bot.maxPerTarget) u *= A.crowdPenalty;
  else if (others > 0) u *= TH.sharedTargetMult;
  // §10.6: near a human, bots brawl with each other 30% of the time.
  if (br.brawlMode && ctx.human[slot]) u *= A.crowdPenalty;
  return u;
}

/** Ledge-hangers and teetering beans are free KOs: the utility formula already rewards exposure. */
function phaseAggression(ctx: Ctx): number {
  const ph = ctx.w.phase;
  const m = ctx.w.cfg.mapId;
  return ph === RoundPhase.SUDDEN_DEATH ? TH.sdAggression[m] ?? 1.35 : ph === RoundPhase.RAMP ? TH.rampAggression[m] ?? 1.15 : 1;
}

/** The nearest landed crate within TUNE.bot.fetch.range, or null. */
function nearestCrate(ctx: Ctx): Entity | null {
  const b = ctx.self;
  let best: Entity | null = null;
  let bd: number = TUNE.bot.fetch.range;
  for (const e of ctx.w.ents) {
    if (e.kind !== EK.CRATE) continue;
    const d = Math.hypot(e.pos.x - b.pos.x, e.pos.y - b.pos.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
}

/**
 * Whether this bot has its eye on bomb `e` (rolled once per bomb, by difficulty, less often while
 * busy fighting or off its feet), or its fuse is about to run out (panic: everyone sees that).
 */
function noticesBomb(ctx: Ctx, e: Entity): boolean {
  const br = ctx.brain;
  if (br.bombSeen !== e.id) {
    br.bombSeen = e.id;
    const busy = ctx.self.state !== BS.NORMAL || (br.goal === Goal.ATTACK && br.target !== NO_SLOT);
    // Its own bomb and the crown (everyone's eyes are on the wearer's surroundings) are always watched.
    br.bombNoticed = e.owner === ctx.self.slot || ctx.self.holdsCrown
      || chance(br, (BOT_TUNE.feel.bombNotice[br.p.difficulty] ?? 0.7) * (busy ? BOT_TUNE.feel.bombBusyNotice : 1));
  }
  return br.bombNoticed || (e.data2 === BOMB_LIT && e.timer <= BOT_TUNE.feel.bombPanicFuse);
}

/** A lit bomb within the EVADE range with a short fuse, or one about to land on us (if noticed). */
function threateningBomb(ctx: Ctx): Entity | null {
  const b = ctx.self;
  const ev = TUNE.bot.evade;
  for (const e of ctx.w.ents) {
    if (e.kind !== EK.BOMB || e.data === b.slot) continue;
    const d = Math.hypot(e.pos.x - b.pos.x, e.pos.y - b.pos.y);
    if (d >= Math.max(ev.bombRange, TUNE.bomb.maxRange) || !noticesBomb(ctx, e)) continue;
    if (e.data2 === BOMB_LIT && e.timer < ev.bombFuseTicks && d < ev.bombRange) return e;
    // In flight toward us: where it lands is where it will be lit.
    if (e.data2 !== BOMB_LIT && e.data === NO_SLOT && e.vz !== 0) {
      const tl = Math.max(0, (e.vz + Math.sqrt(Math.max(0, e.vz * e.vz + 2 * ctx.w.rules.gravity * Math.max(0, e.z)))) / ctx.w.rules.gravity);
      const lx = e.pos.x + e.vel.x * tl;
      const ly = e.pos.y + e.vel.y * tl;
      if (Math.hypot(lx - b.pos.x, ly - b.pos.y) < TUNE.bomb.radius && ctx.brain.p.difficulty > 0) return e;
    }
  }
  return null;
}

/** A lit, landed bomb worth picking up and throwing back (Normal and Spicy). */
function grabbableBomb(ctx: Ctx): Entity | null {
  const b = ctx.self;
  if (ctx.brain.p.difficulty === 0) return null;
  for (const e of ctx.w.ents) {
    if (e.kind !== EK.BOMB || e.data !== NO_SLOT || e.data2 !== BOMB_LIT) continue;
    if (e.timer <= BOT_TUNE.feel.bombRegrabMinFuse) continue;
    if (Math.hypot(e.pos.x - b.pos.x, e.pos.y - b.pos.y) < TH.bombGrabRange) return e;
  }
  return null;
}

function resetChoice(c: Choice): Choice {
  c.goal = Goal.WANDER;
  c.target = NO_SLOT;
  c.ent = 0;
  c.u = -1;
  return c;
}

/** One think: score every option and switch goals (§10.2). */
export function think(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  br.lastThink = w.tick;
  br.forceThink = false;
  ctx.tele.thinks++;
  const c = resetChoice(choice);
  // WANDER/POSITION (§10.2 default): worth more right after an exchange (people disengage and
  // reset their spacing) and when the bot's own footing is poor.
  // With few beans left there is nobody to position against: people go and fight.
  const crowd = Math.max(0, Math.min(1, (ctx.alive - 2) / 6));
  let wanderU: number = TH.wanderU * (TH.fewWanderMult + (1 - TH.fewWanderMult) * crowd);
  if (w.tick - br.lastShoveTick < TH.disengageTicks) wanderU += TH.disengageU;
  // (Relative to the ground that exists: on a Crumble Cake island everything is near the milk.)
  if (ctx.field.safety(b.pos.x, b.pos.y) < ctx.margin * TH.lowSafetyFrac) wanderU += TH.lowSafetyWanderU;
  if (b.cdLedge > 0) wanderU += TH.usedLedgeWanderU;
  consider(ctx, c, Goal.WANDER, NO_SLOT, 0, wanderU);

  // Mode options (§10.4); each returns the multiplier for ordinary attacks.
  let attackMult = 1;
  switch (w.cfg.modeId) {
    case ModeId.LOBBY: attackMult = lobbyOptions(ctx, c); break;
    case ModeId.PODIUM: attackMult = podiumOptions(ctx, c); break;
    case ModeId.HOT_POTATO: attackMult = potatoOptions(ctx, c); break;
    case ModeId.CROWN_GRAB: attackMult = crownOptions(ctx, c); break;
    default: break;
  }

  // ATTACK(T).
  if (attackMult > 0) {
    const mean = br.p.prefersStrongest ? meanScore(ctx) : 0;
    const own = ctx.field.safety(b.pos.x, b.pos.y);
    // Rounds open with positioning, not a brawl at the spawn ring: interest ramps in over the first seconds.
    const warm = w.tick < TH.warmupTicks ? TH.warmupStart + (1 - TH.warmupStart) * (Math.max(0, w.tick) / TH.warmupTicks) : 1;
    // A duel (two beans left) heats up: interest grows the longer it lasts, so 1v1s never stall.
    const duelT = ctx.duelSince < 0 ? 0 : w.phase === RoundPhase.SUDDEN_DEATH ? 1 : Math.max(0, Math.min(1, (w.tick - ctx.duelSince - TH.duelDelayTicks) / TH.duelRampTicks));
    const duel = 1 + (TH.duelAggression - 1) * duelT;
    // Boredom: a long spell without a KO draws people into fights.
    const bored = Math.max(0, Math.min(1, (w.tick - ctx.lastKoTick - TH.boredAfterTicks) / TH.boredRampTicks));
    const k = attackMult * phaseAggression(ctx) * warm * duel * (1 + bored * TH.boredAggression) * (TH.mapAggression[w.cfg.mapId] ?? 1) * (own < Math.min(TH.ownSafetyLow, ctx.margin * TH.lowSafetyFrac) ? TH.ownSafetyLowMult : 1);
    for (let s = 0; s < ctx.seen.length; s++) {
      if (s === b.slot || !isEnemy(ctx, s)) continue;
      const u = attackUtility(ctx, s, mean);
      if (u > 0) consider(ctx, c, Goal.ATTACK, s, 0, u * k);
    }
  }

  // ITEM: fetch a crate (§10.2).
  if (b.item === ItemKind.NONE && !b.holdsCrown && w.cfg.itemsOn && w.rules.itemPool.length > 0) {
    const crate = nearestCrate(ctx);
    if (crate && ctx.field.safety(b.pos.x, b.pos.y) >= ctx.margin * BOT_TUNE.feel.attackMarginMult) {
      consider(ctx, c, Goal.ITEM, NO_SLOT, crate.id, TUNE.bot.fetch.utility * br.p.itemGreed);
    }
  }

  // EVADE: bombs and predicted hazards (§10.2; lunges and gloves are reflexes).
  const bomb = threateningBomb(ctx);
  if (bomb) consider(ctx, c, Goal.EVADE, NO_SLOT, bomb.id, TH.evadeBombU);
  else {
    // A telegraphed hazard is noticed after a human delay: the hazard timing error of §10.6.
    const dz = ctx.hz.danger(b.pos.x, b.pos.y, b.radius, true);
    // Crumble Cake: a candle about to puff is escaped by moving to another island (WANDER's anchor
    // choice weighs the flicker), not by a short dash that only finds void.
    if (dz * br.p.hazardRespect >= TUNE.bot.evade.dangerThreshold && w.cfg.mapId !== MapId.CAKE) {
      if (br.dangerSince < 0) br.dangerSince = w.tick + Math.round(Math.abs(gauss(br)) * br.p.hazardSigma * TH.noticeSigmaMult);
      if (w.tick >= br.dangerSince) consider(ctx, c, Goal.EVADE, NO_SLOT, 0, TH.evadeDangerU * dz);
    } else br.dangerSince = -1;
  }

  // BOMB_GRAB: pick up a lit bomb with fuse to spare and throw it back.
  const loose = b.cdGrab === 0 && !b.holdsCrown ? grabbableBomb(ctx) : null;
  if (loose) consider(ctx, c, Goal.BOMB_GRAB, NO_SLOT, loose.id, TH.bombGrabU);

  // Flavor: the victory dance (§10.6) and Gary's teacup ride.
  // (Never with a crown on: the wearer has better things to do than invite a shove.)
  if (br.danceUntil > w.tick && !b.holdsCrown) consider(ctx, c, Goal.DANCE, NO_SLOT, 0, 4);
  if (br.p.gary && w.cfg.mapId === MapId.TEACUP && w.map.cupOccupant === NO_SLOT && w.tick >= br.cupNext && b.cdCup === 0) {
    if (br.goal === Goal.CUP || chance(br, TH.cupChance)) consider(ctx, c, Goal.CUP, NO_SLOT, 0, br.goal === Goal.CUP ? 2 : 1.2);
  }

  // Minimum hold (§10.1): keep the current goal for 18 ticks unless it is no longer possible.
  if (c.goal !== br.goal || c.target !== br.target || c.ent !== br.targetEnt) {
    const young = w.tick - br.goalSince < TUNE.bot.minGoalTicks;
    if (young && stillValid(ctx) && c.goal !== Goal.EVADE) return;
  }
  if (!setGoal(ctx, c.goal, c.target, c.ent, c.u)) setGoal(ctx, Goal.WANDER, NO_SLOT, 0, wanderU);
  if (br.goal === Goal.EVADE && br.targetEnt === 0) {
    bestSafeSpot(ctx, TUNE.bot.evade.safeSearchRadius, SPOT);
    br.gx = SPOT.x;
    br.gy = SPOT.y;
  }
  if (br.goal === Goal.CUP && br.goalSince === w.tick) br.cupNext = w.tick + TH.cupCooldownTicks;
}

/** The current goal can still be pursued (its target or entity still exists). */
function stillValid(ctx: Ctx): boolean {
  const br = ctx.brain;
  if (goalTargetsBean(br.goal) && br.target !== NO_SLOT) return ctx.seen[br.target].body;
  if (br.targetEnt !== 0) {
    for (const e of ctx.w.ents) if (e.id === br.targetEnt) return true;
    return false;
  }
  return true;
}
